/**
 * How far an opportunity or project has got: RFQ → Quoted → Ordered → Invoiced.
 * The steps are EVIDENCE from the server (`app/services/deal_flow.py`): the orders QX raised
 * from the deal's linked quotes, and the invoices of those orders — never typed.
 */
import { escapeHtml as esc } from '@isg/ui-kit';
import { money } from './fig.js';

const ORDER = ['rfq', 'quoted', 'ordered', 'invoiced'];

export function journeyStrip(j, { compact = false } = {}) {
  if (!j) return '';
  const fmt = money(j.currency);
  const reached = ORDER.indexOf(j.step);
  const step = (key, label, detail, title) => {
    const on = reached >= ORDER.indexOf(key);
    return `<span class="sc-journey-step${on ? ' is-on' : ''}" title="${esc(title || '')}">${esc(label)}${
      detail && !compact ? ` <small>${esc(detail)}</small>` : ''}</span>`;
  };
  const q = j.quotes || {};
  const o = j.orders || {};
  const i = j.invoices || {};
  return `<span class="sc-journey">
    ${step('rfq', j.rfqs ? `RFQ${j.rfqs > 1 ? ` ×${j.rfqs}` : ''}` : 'RFQ', '', 'A request logged against it')}
    ${step('quoted', `Quoted${q.count ? ` ×${q.count}` : ''}`, q.value ? fmt(q.value) : '',
      q.pricing ? `${q.qx} in QX, ${q.pricing} built in pricing` : 'Quotes linked to it')}
    ${step('ordered', `Ordered${o.count ? ` ×${o.count}` : ''}`, o.value ? fmt(o.value) : '',
      'Orders QX raised from its quotes')}
    ${step('invoiced', 'Invoiced', i.value ? fmt(i.value) : '', 'Invoices of those orders')}
    ${j.erp === 'unavailable' ? '<small class="sc-list-meta">(ERP not answering)</small>' : ''}
  </span>`;
}

const FLAG = {
  no_next_step: ['amber', 'no next step'],
  next_step_overdue: ['red', 'next step overdue'],
  quiet: ['amber', 'gone quiet'],
  past_close: ['red', 'past close date'],
};

/**
 * CRM #2 — the deal's next step (its soonest open task) and what needs attention, from
 * `deal_flow.health`. One line, for a list row.
 */
export function nextStepLine(h) {
  if (!h) return '';
  const n = h.next_step;
  const badges = (h.flags || []).filter(f => FLAG[f] && !(f === 'next_step_overdue' && n))
    .map(f => `<span class="badge badge-${FLAG[f][0]}"${f === 'quiet' && h.quiet_days
      ? ` title="Nothing logged for ${h.quiet_days} days"` : ''}>${esc(FLAG[f][1])}</span>`).join(' ');
  const step = n ? `<span style="${n.overdue ? 'color:var(--red-600)' : ''}">Next: ${esc(n.title)}${
    n.due ? ` · ${esc(new Date(`${n.due}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }))}` : ''}${
    n.overdue ? ' (overdue)' : ''}</span>` : '';
  return step || badges ? `<div class="sc-list-meta" style="margin-top:2px">${step}${step && badges ? ' ' : ''}${badges}</div>` : '';
}
